import { Injectable, signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  BigQueryUploadJobStatus,
  IBigQueryUploadJob,
  IBigQueryUploadsConfig,
  IBigQueryUploadStats,
} from '@principle-theorem/principle-core/interfaces';
import { sortTimestampAsc, WithRef } from '@principle-theorem/shared';
import { first } from 'lodash';

interface IBigQueryUploadState {
  config?: WithRef<IBigQueryUploadsConfig>;
  jobs: WithRef<IBigQueryUploadJob>[];
  stats: WithRef<IBigQueryUploadStats>[];
}

export interface IStatDelta {
  timeDeltaMs: number;
  jobsTotal: number;
  jobsStarted: number;
  jobsSkipped: number;
  filesTotal: number;
  filesStarted: number;
  filesSkipped: number;
}

@Injectable()
export class BigQueryUploadStore extends ComponentStore<IBigQueryUploadState> {
  config = this.selectSignal((state) => state.config);
  jobs = this.selectSignal((state) => state.jobs);
  selectedStatusFilters = signal<string[]>([]);

  stats = this.selectSignal((state) =>
    state.stats
      .sort((a, b) => sortTimestampAsc(a.evaluatedAt, b.evaluatedAt))
      .reverse()
  );
  latestStat = this.selectSignal(this.stats, (stats) => first(stats));

  constructor() {
    super({
      jobs: [],
      stats: [],
    });
  }

  setConfig(config?: WithRef<IBigQueryUploadsConfig>): void {
    this.patchState({ config });
  }

  setJobs(jobs: WithRef<IBigQueryUploadJob>[]): void {
    this.patchState({ jobs });
  }

  setStats(stats: WithRef<IBigQueryUploadStats>[]): void {
    this.patchState({ stats });
  }

  filterJobsByStatus(
    jobs: WithRef<IBigQueryUploadJob>[],
    status: BigQueryUploadJobStatus | BigQueryUploadJobStatus[]
  ): WithRef<IBigQueryUploadJob>[] {
    const statuses = Array.isArray(status) ? status : [status];
    return jobs.filter((job) => statuses.includes(job.status));
  }

  calculateStatDelta(
    oldestStats?: WithRef<IBigQueryUploadStats>,
    newestStats?: WithRef<IBigQueryUploadStats>
  ): IStatDelta {
    const oldest = this._getZeroDelta(oldestStats);
    const newest = this._getZeroDelta(newestStats);

    return {
      timeDeltaMs: newest.timeDeltaMs - oldest.timeDeltaMs,
      jobsTotal: newest.jobsTotal - oldest.jobsTotal,
      jobsStarted: newest.jobsStarted - oldest.jobsStarted,
      jobsSkipped: newest.jobsSkipped - oldest.jobsSkipped,
      filesTotal: newest.filesTotal - oldest.filesTotal,
      filesStarted: newest.filesStarted - oldest.filesStarted,
      filesSkipped: newest.filesSkipped - oldest.filesSkipped,
    };
  }

  private _getZeroDelta(record?: WithRef<IBigQueryUploadStats>): IStatDelta {
    return {
      timeDeltaMs: record?.evaluatedAt.toMillis() ?? 0,
      jobsTotal: record?.stats.total.jobs ?? 0,
      jobsStarted: record?.stats.success.jobs ?? 0,
      jobsSkipped: record?.stats.skipped.jobs ?? 0,
      filesTotal: record?.stats.total.files ?? 0,
      filesStarted: record?.stats.success.files ?? 0,
      filesSkipped: record?.stats.skipped.files ?? 0,
    };
  }
}
